import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Container,
  Typography,
  makeStyles
} from '@material-ui/core';
import Prism from 'prismjs';
import { useHistory } from 'react-router-dom';
import { ResizeObserver } from 'resize-observer';

import CheckerDemo from './CheckerDemo';
import { PublicNavbar } from '../../components/Navbars';
import auth from '../../libs/auth';


const functionInstructions = `function test1() {
  // Do something...
}

/* 
 * A function named "main" will only be recognized
 * and executed by the system.
 */
function main(input1, input2) {
  test1()
  // Other operations
}
`;

const outputInstructionsGood = `function main() {
  console.log('Final Output') // Single output only
}
`;

const outputInstructionsBad = `function main() {
  // Bad multiple outputs
  console.log('Some other things')
  console.log('Some other things again')

  console.log('Final Output')
}
`;

const useStyle = makeStyles((theme) => ({
  titleText: {
    display: 'inline',
    backgroundColor: theme.palette.primary.contrastText
  },
  sectionWrapper: {
    width: '100%',
    overflow: 'hidden'
  },
  bannerSection: {
    position: 'relative',
    overflow: 'hidden',

    '& .banner-container': {
      display: 'flex',
      position: 'relative',
      flexDirection: 'column-reverse',
      zIndex: 1,

      [theme.breakpoints.up('md')]: {
        flexDirection: 'row'
      },

      '& > *': {
        width: '100%',
        flex: '1 0 0',

        [theme.breakpoints.up('md')]: {
          '&:nth-child(1)': {
            display: 'flex',
            alignItems: 'center',
            paddingBottom: '15vh !important'
          },
          '&:nth-child(2)': {
            paddingTop: '20vh !important'
          }
        },
      }
    }
  },
  contentSection: {
    color: theme.palette.primary.contrastText,
    width: '100%',
    position: 'relative',
    paddingBottom: '200px',

    '& > *': {
      position: 'relative',
      zIndex: 2
    },

    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: '170vw',
      height: '120vw',
      left: '-20vw',
      top: '-150px',
      backgroundColor: theme.palette.primary.main,
      borderTopLeftRadius: '25% 25%',
      borderTopRightRadius: '75% 100%',
      zIndex: 0,

      [theme.breakpoints.up('md')]: {
        top: '-40vh',
      }
    },

    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: '15vw',
      backgroundColor: theme.palette.primary.main,
      zIndex: 0
    },

    '& p': {
      fontSize: '1.3rem',
      textAlign: 'center',
    },

    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontWeight: 'bold',
      textAlign: 'center',
    },

    '& .pre-code': {
      color: 'black',
      borderRadius: '20px'
    },
  },
  footerSection: {
    padding: '100px 0',
    '& img': {
      width: '40vw',
      maxWidth: '300px'
    }
  },
  sectionItem: {
    paddingTop: '100px',
    position: 'relative',

    '&:first-child': {
      paddingTop: 0,
    },

    '& .background-icon': {
      maxHeight: '300px',
      maxWidth: '300px',
      position: 'absolute',
      left: 0,
      top: '30px',
      opacity: '0.1',

      '&.right': {
        left: 'auto',
        right: 0
      }
    }
  }
}));

const Home = () => {
  const [demoMinHeight, setDemoMinHeight] = useState<string>('80vh');
  const classes = useStyle();
  const history = useHistory();

  const getNavbar = () => window.document.querySelector('.app-navbar');

  const recalculateDemoMinHeight = () => {
    const navbar = getNavbar();
    if (navbar) {
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      setDemoMinHeight(vw < 960 ? '' : `${vh - navbar.clientHeight - 80}px`);
    }
  };

  useEffect(() => {
    if (auth.isStudent()) history.push('/student/courses');
    else if (auth.isAdmin()) history.push('/admin/courses');
  }, []);

  // observe document size the resize banner demo height;
  useEffect(() => {
    const docSizeObserver = new ResizeObserver(recalculateDemoMinHeight);
    docSizeObserver.observe(window.document.body);
    window.addEventListener('resize', recalculateDemoMinHeight);
    return () => {
      docSizeObserver.disconnect();
      window.removeEventListener('resize', recalculateDemoMinHeight);
    };
  }, []);

  return (
    <Box>
      <PublicNavbar />
      <Box className={classes.sectionWrapper}>
        <Box className={classes.bannerSection}>
          <Container className="banner-container">
            <div className="py-4 px-3" style={{minHeight: demoMinHeight}}>
              <CheckerDemo />
            </div>

            <div className="py-4 px-3 text-right">
              <Typography variant="h3" color="primary" className={classes.titleText}>
                <strong>WELCOME, KINGSLAND STUDENT!</strong>
              </Typography>
              <Typography variant="h5" color="textSecondary">
                This is the automated exam checker platform of Kingsland University.
              </Typography>
            </div>
          </Container>
        </Box>

        <Box className={classes.contentSection}>
          <Container maxWidth="md">

            <Box className={classes.sectionItem}>
              <Typography variant="h4">ABOUT THE PLATFORM</Typography>

              <Box pt={5}>
                <Typography>
                  This is where you’ll submit your programming exercises, homeworks, and exams as directed by your instructor.
                </Typography>
              </Box>

              <Box pt={3}>
                <Typography>
                  The platform will provide automated feedback if your code properly covers expected scenarios as well as edge cases - making you feel confident that your submission is correct and ready for grading.
                </Typography>
              </Box>
            </Box>


            <Box className={classes.sectionItem}>
              <img className="background-icon right" src="/assets/svg-icons/edit-solid.svg" alt="Background Icon" />

              <Typography variant="h4">INPUTS</Typography>

              <Box pt={5}>
                <Typography>
                  Your inputs are specified in the exercise document and are automatically supplied in the parameters of your main function.
                </Typography>
              </Box>
            </Box>


            <Box className={classes.sectionItem}>
              <img className="background-icon" src="/assets/svg-icons/f(x).svg" alt="Background Icon" />

              <Typography variant="h4">FUNCTIONS</Typography>

              <Box pt={5}>
                <Typography>
                  There can be as many functions as you want, but be sure to declare your main function.
                </Typography>
              </Box>

              <Box pt={5}>
                <Container maxWidth="sm">
                   <pre className="pre-code">
                  <code
                    dangerouslySetInnerHTML={{
                      __html: Prism.highlight(
                        functionInstructions,
                        Prism.languages.javascript,
                        'javascript',
                      ),
                    }}
                  />
                </pre>
                </Container>
              </Box>

              <Box pt={5}>
                <Typography>
                  It is important that you have a <b>main()</b> function which will serve as the starting point of your app.
                </Typography>
              </Box>
            </Box>


            <Box className={classes.sectionItem}>
              <img className="background-icon right" src="/assets/svg-icons/clipboard.svg" alt="Background Icon" />
              <Typography variant="h4">OUTPUTS</Typography>

              <Box pt={5}>
                <Typography>
                  There can be as many functions as you want, but be sure to declare your main function.
                </Typography>
              </Box>
            </Box>


            <Box className={classes.sectionItem}>
              <img className="background-icon" src="/assets/svg-icons/like.svg" alt="Background Icon" />
              <Typography variant="h4">GOOD SUBMISSION</Typography>

              <Box pt={5}>
                <Container maxWidth="sm">
                  <pre className="pre-code">
                    <code
                      dangerouslySetInnerHTML={{
                        __html: Prism.highlight(
                          outputInstructionsGood,
                          Prism.languages.javascript,
                          'javascript',
                        ),
                      }}
                    />
                  </pre>
                </Container>
              </Box>
            </Box>


            <Box className={classes.sectionItem}>
              <img className="background-icon right" src="/assets/svg-icons/dislike.svg" alt="Background Icon" />
              <Typography variant="h4">BAD SUBMISSION</Typography>

              <Box pt={5}>
                <Container maxWidth="sm">
                  <pre className="pre-code">
                    <code
                      dangerouslySetInnerHTML={{
                        __html: Prism.highlight(
                          outputInstructionsBad,
                          Prism.languages.javascript,
                          'javascript',
                        ),
                      }}
                    />
                  </pre>
                </Container>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>

      <Box  className={classes.footerSection}>
        <Grid container justify="center">
          <Grid item>
            <Box px={5} py={2}>
              <img src="/banner.png" alt="Kingsland University" />
            </Box>
          </Grid>
          <Grid item>
            <Box px={5} py={2}>
              <img src="/assets/logos/Kingsland_InnovationCentre_Teal_HorizontalwK__2_.png" alt="Kingsland Innovation Center" />
            </Box>
          </Grid>
        </Grid>

        <Box pt={5}>
          <Typography color="textSecondary" align="center">
            &copy; Kingsland University
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
